import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Sale } from "@/components/atomic-crm/types";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { iniciales } from "../negocios/formato";
import { reducirFoto } from "./reducirFoto";
import { useUrlFoto } from "./useUrlFoto";

/**
 * Foto de perfil (30, Robert 09–13/10/2026): bucket privado `avatares`, ruta en `sales.foto`, se ve
 * con enlaces firmados de una hora. Sin la 30 en la base, la fila de sales no trae `foto` y todo
 * sigue con la inicial.
 */
/** La foto o, si no hay, la inicial (como hasta ahora). */
export const AvatarUsuario = ({
  nombre,
  foto,
  className,
  colorFondo,
}: {
  nombre: string;
  foto?: string | null;
  className?: string;
  colorFondo?: string;
}) => {
  const url = useUrlFoto(foto);
  const base = cn(
    "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold",
    className,
  );
  if (url) {
    return (
      <span className={base} data-testid="foto-usuario">
        <img src={url} alt={nombre} className="size-full object-cover" />
      </span>
    );
  }
  return (
    <span
      className={cn(base, !colorFondo && "bg-[#0A0A0A] text-[#FAFAF7]")}
      style={
        colorFondo ? { backgroundColor: colorFondo, color: "#fff" } : undefined
      }
      aria-hidden
    >
      {iniciales(nombre).slice(0, 2) || "·"}
    </span>
  );
};

/** Subir, cambiar o quitar la foto (uno la suya; Jan y Robert, la de cualquiera). */
export const EditorFoto = ({
  usuario,
}: {
  usuario: Sale & { foto?: string | null };
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const entrada = useRef<HTMLInputElement>(null);
  const [guardando, setGuardando] = useState(false);
  const nombre =
    `${usuario.first_name ?? ""} ${usuario.last_name ?? ""}`.trim();

  const refrescar = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["sales"] }),
      queryClient.invalidateQueries({ queryKey: ["nevora", "fotoUsuario"] }),
    ]);

  const subir = async (fichero: File) => {
    setGuardando(true);
    try {
      const reducida = await reducirFoto(fichero);
      const ruta = await dataProvider.subirFotoUsuario(usuario.id, reducida);
      await dataProvider.cambiarFotoUsuario(usuario.id, ruta);
      if (usuario.foto)
        await dataProvider
          .borrarFicheroFoto(usuario.foto)
          .catch(() => undefined);
      await refrescar();
      notify("nevora.usuarios.foto_guardada", { type: "success" });
    } catch {
      notify("nevora.usuarios.errores.foto", { type: "error" });
    } finally {
      setGuardando(false);
      if (entrada.current) entrada.current.value = "";
    }
  };

  const quitar = async () => {
    if (!usuario.foto) return;
    setGuardando(true);
    try {
      await dataProvider.cambiarFotoUsuario(usuario.id, null);
      await dataProvider.borrarFicheroFoto(usuario.foto).catch(() => undefined);
      await refrescar();
      notify("nevora.usuarios.foto_quitada", { type: "success" });
    } catch {
      notify("nevora.usuarios.errores.foto", { type: "error" });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid="editor-foto">
      <AvatarUsuario
        nombre={nombre || usuario.email}
        foto={usuario.foto}
        className="size-12 text-sm"
      />
      <div className="space-y-1">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={guardando}
            onClick={() => entrada.current?.click()}
          >
            {guardando ? (
              <Loader2 className="size-4 animate-spin mr-1.5" />
            ) : null}
            {translate("nevora.usuarios.cambiar_foto")}
          </Button>
          {usuario.foto ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={guardando}
              onClick={quitar}
            >
              {translate("nevora.usuarios.quitar_foto")}
            </Button>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">
          {translate("nevora.usuarios.foto_ayuda")}
        </p>
        <input
          ref={entrada}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          data-testid="entrada-foto"
          aria-label={translate("nevora.usuarios.foto")}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void subir(f);
          }}
        />
      </div>
    </div>
  );
};
